import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const HowItWorksContainer = styled.section`
  padding: 120px 5% 80px;
  background: #0a0a0a;
  
  @media (max-width: 768px) {
    padding: 80px 20px 60px;
  }
`;

const HowItWorksContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionHeader = styled.div`
  margin-bottom: 80px;
  text-align: left;
`;

const SectionTitle = styled(motion.h2)`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: clamp(28px, 3.5vw, 40px);
  font-weight: 400;
  color: white;
  line-height: 1.3;
  letter-spacing: -0.01em;
  margin-bottom: 16px;
`;

const SectionSubtitle = styled(motion.p)`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 15px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.5);
  line-height: 1.6;
  max-width: 600px;
  margin: 0;
`;

const StepsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 60px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const StepCard = styled(motion.div)`
  text-align: left;
  position: relative;
`;

const StepNumber = styled.div`
  display: none;
`;

const StepTitle = styled.h3`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 16px;
  font-weight: 500;
  color: white;
  margin-bottom: 12px;
  line-height: 1.4;
`;

const StepDescription = styled.p`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.5);
  line-height: 1.6;
  margin: 0;
`;

const HowItWorks = () => {
  const steps = [
    {
      number: "1",
      title: "Connect your platforms",
      description: "Link your streaming services, social media accounts, and business tools. DIZEE securely connects to your existing workflow in minutes."
    },
    {
      number: "2", 
      title: "AI learns your business",
      description: "Our AI analyzes your data patterns, fan behavior, and industry trends to understand your unique music business and goals."
    },
    {
      number: "3",
      title: "Automate and optimize",
      description: "Set up workflows, get intelligent insights, and let DIZEE handle routine tasks while you focus on creating and performing."
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const stepVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <HowItWorksContainer>
      <HowItWorksContent>
        <SectionHeader>
          <SectionTitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            How it works
          </SectionTitle>
        </SectionHeader>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <StepsContainer>
            {steps.map((step, index) => (
              <StepCard
                key={index}
                variants={stepVariants}
              >
                <StepNumber>{step.number}</StepNumber>
                <StepTitle>{step.title}</StepTitle>
                <StepDescription>{step.description}</StepDescription>
              </StepCard>
            ))}
          </StepsContainer>
        </motion.div>
      </HowItWorksContent>
    </HowItWorksContainer>
  );
};

export default HowItWorks; 